/**
 * The calendar (fase 2, steg 3).
 *
 * ---------------------------------------------------------------------------------------------
 * THE DENSITY DECIDED THE SHAPE, AND IT WAS MEASURED BEFORE ANYTHING WAS DESIGNED.
 *
 * The seeded organisation has 4,299 tasks with a deadline, and 1,475 of them fall in October
 * alone — roughly fifty per day. A month grid cannot list fifty things in a cell, and an endpoint
 * that returned them would ship 1,475 rows to draw thirty numbers.
 *
 * So the month view asks for COUNTS PER DAY, and the day itself is a second, ordinary request
 * against the task list. That is why `listTasksQuerySchema` gains `dueFrom` / `dueTo` in the same
 * change: the day detail is not a special endpoint, it is the list everybody already has, asked
 * for one day.
 *
 * THE COUNTS GO THROUGH THE SAME SCOPE AS THE LIST, AND THIS IS THE PART THAT MATTERS.
 * `task:list` is `own` for `limited` and `grant` for `external`. A count computed without that
 * narrowing would tell somebody who may see three tasks that fifty exist — the number itself is
 * the leak, and it leaks precisely on the days that are busy. The same `QueryScope` the task list
 * builds must reach this query, and the integration tests should assert a limited user's calendar
 * against their own task count rather than against the organisation's.
 *
 * EDITIONS ARE BANDS, NOT DAYS. An edition has `starts_on` and `ends_on`, so it belongs to a
 * RANGE of the window rather than to one cell, and the response keeps it whole rather than
 * exploding it into a row per day. The client draws the band; the server does not decide how many
 * days it covers, because a range clipped to the window would lose the fact that it began before.
 *
 * WHAT IS DELIBERATELY NOT HERE. Performances, shifts and calendar entries of their own do not
 * exist yet; when they do they become new keys beside `days` and `editions`, which is why the
 * response is an object and not an array. And there is no year scoping: `/tasks` is the
 * organisation's list, so the organisation's calendar is its counterpart. A festival-scoped
 * calendar arrives with the year picker, on the pages that have one.
 * ---------------------------------------------------------------------------------------------
 */
import { z } from 'zod';
import { pageCursorFields, pageQuerySchema } from '../http/pagination.js';

import { booleanQuerySchema } from './common.js';

/** A calendar day, with no time and no zone — the same shape `editions.starts_on` uses. */
const dateOnly = z.iso.date();

/**
 * The widest window a single request may ask for.
 *
 * Three months. Wide enough for a quarter view and for a month view that pre-fetches its
 * neighbours; narrow enough that nobody can ask for five years and get a scan of the whole task
 * table. A range longer than this is a 400 rather than a slow answer.
 */
export const CALENDAR_MAX_DAYS = 92;

export const calendarQuerySchema = z
  .object({
    from: dateOnly,
    to: dateOnly,
    /**
     * Absent means every edition's tasks and the organisation's unscoped ones — the "totalkalender"
     * the build plan asks for. Present narrows to one year, which is what a festival-scoped
     * calendar will send.
     */
    editionId: z.uuid().optional(),
  })
  .refine((q) => q.to >= q.from, {
    message: 'The window must end on or after it starts.',
    path: ['to'],
  })
  .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 + 1 <= CALENDAR_MAX_DAYS, {
    message: `A window may not be longer than ${CALENDAR_MAX_DAYS} days.`,
    path: ['to'],
  })
  .meta({ id: 'CalendarQuery' });

export const calendarDaySchema = z
  .object({
    date: dateOnly,
    /** Tasks due on this day that the CALLER may see. Never the organisation's total. */
    taskCount: z.number().int().nonnegative(),
    /**
     * Of those, how many are past their deadline and not finished. Drawn differently, and counted
     * on the server because "overdue" depends on the day the question is asked, not on the day
     * being drawn.
     */
    overdueCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'CalendarDay' });

export const calendarEditionBandSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    festivalId: z.uuid(),
    festivalName: z.string(),
    /** The edition's own dates, NOT clipped to the window — see the header. */
    startsOn: dateOnly,
    endsOn: dateOnly,
  })
  .meta({ id: 'CalendarEditionBand' });

export const calendarResponseSchema = z
  .object({
    /**
     * One entry per day IN THE WINDOW THAT HAS SOMETHING, not one per calendar day. A month with
     * nothing in its first week returns 24 entries, and the client draws the empty cells itself —
     * it knows the window it asked for.
     */
    days: z.array(calendarDaySchema),
    /** Every edition whose range overlaps the window, whole. */
    editions: z.array(calendarEditionBandSchema),
  })
  .meta({ id: 'CalendarResponse' });

export type CalendarQuery = z.infer<typeof calendarQuerySchema>;
export type CalendarDay = z.infer<typeof calendarDaySchema>;
export type CalendarEditionBand = z.infer<typeof calendarEditionBandSchema>;
export type CalendarResponse = z.infer<typeof calendarResponseSchema>;

/* ================================================================================================
 * KOI DEL 3 — FRA TELLINGER TIL ELEMENTER
 *
 * Alt over er månedsrutenettets TALL per dag, og det blir stående: femti oppgaver i en celle er et
 * tall, ikke en liste. Under er ELEMENTENE, som er det uke, dag og agenda tegner — og som måneden
 * bruker til de inntil fire den viser per dag.
 *
 * Filhodet over spådde nøyaktig dette: «Performances, shifts and calendar entries of their own do
 * not exist yet; when they do they become new keys beside days and editions.» Det er det som skjer
 * her, og derfor er elementene en egen lesning ved siden av tellingene framfor å erstatte dem.
 * ============================================================================================= */

/**
 * De sju kildene, som ordren navngir dem.
 *
 * `deadline` er den ene som ikke er en tabell: den er en DATO på noe annet — eksklusiviteten i en
 * kontrakt, fristen på et bud — og den finnes fordi en frist er noe man planlegger mot selv om
 * ingen «har» den.
 */
export const CALENDAR_KINDS = [
  'task',
  'performance',
  'shift',
  'allocation',
  'edition',
  'deadline',
  'event',
  /*
   * PERSONENS EGNE, PÅ TVERS AV HUS (sak 216, 0385): konsertene, reisene hen sitter på eller kjører,
   * og overnattingene hen har rom i. Bare i «Min kalender», og lest med personens port, ikke husets.
   */
  'concert',
  'travel',
  'stay',
  /*
   * BYRÅETS BOOKINGER (sak 217.26): egne og godtatte forespørsler, som heldag. Bare i et byråhus, og
   * valget om laget vises huskes per bruker sammen med de andre typefiltrene. Aldri honorar.
   */
  'booking',
  /* SPILLESTEDET (agent A): husets kvelder, og arrangørens konserter (tabellen concerts). */
  'night',
  'show',
  /*
   * DET DELTE MED MEG (sak 232, T5): en deling mottakeren har lagt i kalenderen. `id` er DELINGENS,
   * så raden åpnes gjennom `/delt/$grantId`, og `status` bærer objekttypen. Bare i «Min kalender».
   */
  'shared',
] as const;
export type CalendarKind = (typeof CALENDAR_KINDS)[number];
export const calendarKindSchema = z.enum(CALENDAR_KINDS);

/**
 * Hvilket punkt på kilden raden er.
 *
 * En oppgave med både start og frist er TO punkter i kalenderen og én rad i lista, og den som
 * tegner trenger å vite hvilket av dem den har. `slot` er også det som gjør nøkkelen unik: to rader
 * med samme id skilles av den.
 */
export const CALENDAR_SLOTS = [
  'start',
  'due',
  'set',
  'timetable',
  'shift',
  'allocation',
  'edition',
  'exclusivity',
  'bid_hold',
  'event',
  'concert',
  'travel',
  'stay',
  'booking',
  'night',
  'show',
  'shared',
] as const;
export type CalendarSlot = (typeof CALENDAR_SLOTS)[number];

export const calendarItemSchema = z
  .object({
    kind: calendarKindSchema,
    slot: z.enum(CALENDAR_SLOTS),
    id: z.uuid(),
    title: z.string(),
    /** Linja under tittelen: avdelingen, scenen, artisten — hva kilden har. */
    secondary: z.string().nullable(),
    startsAt: z.iso.datetime(),
    /** NULL er et punkt uten varighet: en frist, en oppgavestart. */
    endsAt: z.iso.datetime().nullable(),
    allDay: z.boolean(),
    editionId: z.uuid().nullable(),
    departmentId: z.uuid().nullable(),
    personId: z.uuid().nullable(),
    status: z.string().nullable(),
    /**
     * KAN DEN DRAS?
     *
     * Ordren: «dra flytter hendelser og oppgavefrister (ikke programposter og skift — de flyttes
     * der de bor; vis som låst)». Serveren svarer på det, og ikke klienten, fordi svaret er en
     * EGENSKAP VED KILDEN: en konsert flyttes på lineup-siden uansett hvem som ser den.
     */
    locked: z.boolean(),
    /** Satt bare på en serie som er brettet ut: hvilken hendelse forekomsten kommer fra. */
    occurrenceOf: z.uuid().nullable(),
    /**
     * HVOR MANGE MENNESKER RADEN GJELDER — «Rigg scene · 3» (rettelse A5).
     *
     * NULL for alt som ikke har bemanning: en kontraktsfrist har ingen, og 0 ville vært en påstand
     * om at den har null. Tallet erstatter radene som forsvant da skiftet ble én blokk framfor én
     * per person (migrasjon 0241).
     */
    peopleCount: z.number().int().nullable(),
    /**
     * ARTISTEN EN BOOKING HØRER TIL (QA 26. september, C5). Bare på `booking`: raden åpnes i
     * artistens Bookinger-fane, og id-en alene sier ikke hvilken artist. Valgfri, så de andre
     * kildene ikke må bære et felt de aldri har.
     */
    artistId: z.uuid().nullable().optional(),
    /** Spillestedet et arrangement hører til: det åpnes på /venue/$festivalId/events/$id. */
    festivalId: z.uuid().nullable().optional(),
  })
  .meta({ id: 'CalendarItem' });

/**
 * «Organisasjonen» som en verdi i arrangementlista (QA13 runde 2, #39).
 *
 * Et element uten årgang — en oppgave på org-nivå, et møte i huset — har `edition_id = null`, og
 * null kan ikke stå i en liste over id-er man har krysset av. Ordet er derfor verdien, og det er
 * kodens og aldri en visningstekst: skjermen skriver «Organisasjonen» fra i18n.
 */
export const ORGANISATION_ONLY = 'organisation';

export const calendarItemsQuerySchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    editionId: z.uuid().optional(),
    /**
     * «Min kalender»: bare det som er MITT — mine skift, mine oppgaver, mine invitasjoner.
     *
     * `booleanQuerySchema` OG IKKE `z.boolean()`, og det er ikke pynt: dette er et SPØRREPARAMETER,
     * og et spørreparameter er tekst. `?mine=true` mot en `z.boolean()` er en 400 — som er nøyaktig
     * det første utgave svarte nettleseren med. Den samme koersjonen innboksen og varslene bruker.
     */
    mine: booleanQuerySchema.optional(),
    /**
     * Filterchipsene. Utelatt betyr alle sju.
     *
     * `?kinds=task,event` kommer som ÉN streng, og en `z.array()` uten denne forbehandlingen ville
     * avvist den. Samme form som `listMapPointsQuerySchema.types` — et array sluppet gjennom, en
     * kommaliste delt opp, og en tom streng lest som fravær framfor som en tom liste.
     */
    kinds: z
      .preprocess((value) => {
        if (value === undefined || value === null) return undefined;
        if (Array.isArray(value)) return value;
        const text = String(value).trim();
        return text.length === 0 ? undefined : text.split(',');
      }, z.array(calendarKindSchema).min(1).max(CALENDAR_KINDS.length).optional())
      .optional(),
    departmentId: z.uuid().optional(),
    personId: z.uuid().optional(),
    q: z.string().trim().min(1).max(200).optional(),
    /**
     * HVILKE ARRANGEMENTER MAN VIL SE (QA13 runde 2, #39).
     *
     * Atle: «Det skal finnes et valg for hvilke arrangementer man ser i org-kalenderen og i
     * Oppgaver: avkrysningsliste med "Organisasjonen", hver festival/venue og årgang, pluss
     * hurtigvalgene "Alle" og "Bare organisasjonen".»
     *
     * `ORGANISATION_ONLY` ER EN VERDI I LISTA og ikke et eget flagg, fordi «organisasjonen» er ett
     * av valgene i den samme avkryssingslista. En egen boolean ville betydd to kontroller for ett
     * spørsmål — og «Bare organisasjonen» ville måttet skru av det ene og på det andre.
     *
     * UTELATT BETYR ALLE, som `kinds`. En adresse som bærer standarden ser filtrert ut.
     *
     * Dette er et FILTER på svaret og ikke argumentet til `app.calendar_items`: funksjonen tar én
     * årgang, og den brukes fortsatt av årgangens EGEN kalender. Å utvide signaturen ville vært en
     * migrering for noe det ytre `where` kan gjøre like presist.
     */
    editionIds: z
      .preprocess(
        (value) => {
          if (value === undefined || value === null) return undefined;
          if (Array.isArray(value)) return value;
          const text = String(value).trim();
          return text.length === 0 ? undefined : text.split(',');
        },
        z
          .array(z.union([z.uuid(), z.literal(ORGANISATION_ONLY)]))
          .min(1)
          .max(50)
          .optional(),
      )
      .optional(),
    /*
     * §8.3: markør over vinduet, sortert på (start, type, id). Standard er taket (`MAX_CALENDAR_ITEMS`,
     * 2000 — tallet står her fordi konstanten er definert lenger ned), så nettsiden får hele uka.
     */
    ...pageQuerySchema(2000).shape,
  })
  .refine((query) => Date.parse(query.to) > Date.parse(query.from), {
    message: 'The window must end after it starts.',
    path: ['to'],
  })
  .meta({ id: 'CalendarItemsQuery' });

export const calendarItemsResponseSchema = z
  .object({
    items: z.array(calendarItemSchema),
    ...pageCursorFields,
    /**
     * Sant når vinduet traff taket. Kalenderen viser det som en linje og ikke som en stille
     * avkorting: en uke som mangler halvparten av skiftene sine er verre enn en uke som sier fra.
     */
    truncated: z.boolean(),
  })
  .meta({ id: 'CalendarItemsResponse' });

/** Taket per vindu. En uke med 500 skift er 500 elementer; et år er ikke et vindu. */
export const MAX_CALENDAR_ITEMS = 2000;

/* ------------------------------------------------------------------------------- hendelsene */

export const CALENDAR_ATTENDEE_STATUSES = ['invited', 'accepted', 'declined', 'tentative'] as const;
export type CalendarAttendeeStatus = (typeof CALENDAR_ATTENDEE_STATUSES)[number];

export const calendarAttendeeSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string(),
    status: z.enum(CALENDAR_ATTENDEE_STATUSES),
    respondedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'CalendarAttendee' });

/** Hva en hendelse kan henge på. Polymorf uten fremmednøkkel, som kartpunktene. */
export const CALENDAR_LINK_TYPES = [
  'task',
  'performance',
  'resource',
  'shift',
  'contact',
  'stage',
] as const;
export type CalendarLinkType = (typeof CALENDAR_LINK_TYPES)[number];

export const calendarEventDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    allDay: z.boolean(),
    location: z.string().nullable(),
    /**
     * STEDET SOM EN RESSURS (0938, QA13 #82): «Sted» er ÉN ting, en ressurs eller en adresse. Satt:
     * hendelsen står på ressursen, og i en årgang er den en reservasjon der. Null: «location» er
     * adressen (fritekst), slik den alltid har vært.
     */
    locationResourceId: z.uuid().nullable(),
    /** Ressursens navn nå (ikke tekstkopien i «location»). Null uten ressurs, eller når den er slettet. */
    locationResourceName: z.string().nullable(),
    rrule: z.string().nullable(),
    /** Forekomster som er tatt bort fra serien (EXDATE). Tom for en enkelthendelse. */
    excludedStarts: z.array(z.iso.datetime()),
    linkedType: z.enum(CALENDAR_LINK_TYPES).nullable(),
    linkedId: z.uuid().nullable(),
    /**
     * Navnet på det hendelsen er koblet til — oppgavens tittel, programpostens artist. Null når
     * den ikke er koblet, eller når tingen er slettet (koblingen har ingen fremmednøkkel).
     */
    linkedLabel: z.string().nullable(),
    attendees: z.array(calendarAttendeeSchema),
    /**
     * Den som laget hendelsen (245.18): panelet spør `event:update`/`event:delete` om DENNE raden,
     * og for Begrenset (og medarbeiderens sletting) er svaret «egen» — laget av meg eller invitert.
     */
    createdByPersonId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'CalendarEvent' });

const eventBody = {
  editionId: z.uuid().nullish(),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4000).nullish(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime().nullish(),
  allDay: z.boolean().optional(),
  location: z.string().trim().max(200).nullish(),
  /** Ressursen hendelsen står på (0938). Satt erstatter den adressen: «location» blir ressursens navn. */
  locationResourceId: z.uuid().nullish(),
  rrule: z.string().trim().min(1).max(500).nullish(),
  linkedType: z.enum(CALENDAR_LINK_TYPES).nullish(),
  linkedId: z.uuid().nullish(),
  /** Hvem som inviteres. Erstatter hele lista — se tjenesten om hvorfor det er riktig her. */
  attendeePersonIds: z.array(z.uuid()).max(100).optional(),
};

export const createCalendarEventRequestSchema = z
  .object(eventBody)
  .refine((body) => body.endsAt == null || Date.parse(body.endsAt) >= Date.parse(body.startsAt), {
    message: 'An event may not end before it starts.',
    path: ['endsAt'],
  })
  .meta({ id: 'CreateCalendarEventRequest' });

export const updateCalendarEventRequestSchema = z
  .object({
    editionId: eventBody.editionId,
    title: eventBody.title.optional(),
    description: eventBody.description,
    startsAt: eventBody.startsAt.optional(),
    endsAt: eventBody.endsAt,
    allDay: eventBody.allDay,
    location: eventBody.location,
    locationResourceId: eventBody.locationResourceId,
    rrule: eventBody.rrule,
    linkedType: eventBody.linkedType,
    linkedId: eventBody.linkedId,
    attendeePersonIds: eventBody.attendeePersonIds,
  })
  .meta({ id: 'UpdateCalendarEventRequest' });

/** Svaret fra en deltaker. `invited` er ikke et svar — det er fraværet av ett. */
export const respondToCalendarEventRequestSchema = z
  .object({ status: z.enum(['accepted', 'declined', 'tentative']) })
  .meta({ id: 'RespondToCalendarEventRequest' });

/**
 * HVILKE FOREKOMSTER AV EN SERIE EN ENDRING ELLER SLETTING GJELDER (F49): «bare denne», «denne og
 * senere» eller «alle». `this` og `following` krever `occurrence` — forekomsten som ble klikket.
 */
export const CALENDAR_SERIES_SCOPES = ['this', 'following', 'all'] as const;
export type CalendarSeriesScope = (typeof CALENDAR_SERIES_SCOPES)[number];

/**
 * `DELETE /calendar/events/:id`. Med `occurrence` slettes bare den forekomsten av serien som
 * starter da (en EXDATE), eller med `scope=following` den og alle etter; uten slettes hele
 * hendelsen — for en serie, alle forekomstene.
 */
export const removeCalendarEventQuerySchema = z
  .object({
    occurrence: z.iso.datetime().optional(),
    scope: z.enum(CALENDAR_SERIES_SCOPES).optional(),
  })
  .meta({ id: 'RemoveCalendarEventQuery' });

/**
 * `PATCH /calendar/events/:id` på én forekomst av en serie (F49). Uten `occurrence` endres
 * hendelsen selv, som før. Med `occurrence`: `this` (standard) blir en egen hendelse og et hull i
 * serien, `following` deler serien i to, og `all` endrer serien — en flyttet tid flyttes med like
 * mye som forekomsten ble flyttet.
 */
export const updateCalendarEventQuerySchema = z
  .object({
    occurrence: z.iso.datetime().optional(),
    scope: z.enum(CALENDAR_SERIES_SCOPES).optional(),
  })
  .meta({ id: 'UpdateCalendarEventQuery' });

/* ------------------------------------------------------------------------------ ICS-abonnementet */

/**
 * ÉN LENKE PER GRUPPE (sak 216.13), og hver blir en egen kalender hos abonnenten:
 * `personal` er «mine ting» i alle hus, `concerts` konsertene med reiser og overnatting,
 * `organization` ett hus og `edition` én årgang — begge lest med rollen personen har der.
 */
export const CALENDAR_FEED_GROUPS = ['personal', 'concerts', 'organization', 'edition'] as const;
export const calendarFeedGroupKindSchema = z.enum(CALENDAR_FEED_GROUPS);

/**
 * «LAG NY» TREKKER DEN GAMLE I SAMME GRUPPE, alltid (analysen §3.3 punkt 2). Personlig og Konserter
 * er personens og trekkes i alle hus; huset og årgangen i huset de gjelder.
 */
export const createCalendarFeedTokenRequestSchema = z
  .object({
    group: calendarFeedGroupKindSchema,
    /** Påkrevd for `edition`, forbudt ellers. */
    editionId: z.uuid().nullish(),
    label: z.string().trim().max(80).nullish(),
  })
  .superRefine((value, ctx) => {
    const hasEdition = typeof value.editionId === 'string';
    if (hasEdition !== (value.group === 'edition')) {
      ctx.addIssue({
        code: 'custom',
        path: ['editionId'],
        message: 'An edition link names its edition, and no other link does.',
      });
    }
  })
  .meta({ id: 'CreateCalendarFeedTokenRequest' });

/** Lenken man har, uten selve nøkkelen — den finnes bare i svaret fra `create`. */
export const calendarFeedTokenSummarySchema = z
  .object({
    id: z.uuid(),
    group: calendarFeedGroupKindSchema,
    editionId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    /** Sist en kalender hentet den. Null er «aldri brukt» — en lenke ingen har lagt inn. */
    lastUsedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'CalendarFeedTokenSummary' });

/**
 * EN GRUPPE PERSONEN KAN ABONNERE PÅ, med den levende lenken (eller ingen).
 *
 * `name` er data — husets eller årgangens navn — og null for Personlig og Konserter, som klienten
 * setter ord på selv. API-et returnerer ingen oversatt tekst.
 */
export const calendarFeedGroupSchema = z
  .object({
    group: calendarFeedGroupKindSchema,
    organizationId: z.uuid().nullable(),
    editionId: z.uuid().nullable(),
    name: z.string().nullable(),
    token: calendarFeedTokenSummarySchema.nullable(),
  })
  .meta({ id: 'CalendarFeedGroup' });

/** Ingen parametre: gruppene er personens og det aktive husets. */
export const listCalendarFeedTokensQuerySchema = z.object({}).meta({
  id: 'ListCalendarFeedTokensQuery',
});

export const calendarFeedTokenListSchema = z
  .object({ items: z.array(calendarFeedGroupSchema) })
  .meta({ id: 'CalendarFeedTokenList' });

/**
 * SVARET BÆRER HELE URL-EN, OG BARE ÉN GANG.
 *
 * Nøkkelen lagres hashet (0224), så dette er eneste øyeblikk den finnes i klartekst. Skjermen som
 * viser den sier det — den samme formen invitasjonslenken har.
 */
export const calendarFeedTokenDtoSchema = z
  .object({
    id: z.uuid(),
    url: z.string(),
    group: calendarFeedGroupKindSchema,
    editionId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'CalendarFeedToken' });

export type CalendarItem = z.infer<typeof calendarItemSchema>;
/**
 * `limit` er valgfri i typen (§8.3): ICS-feeden og tjenestens egne kall leser hele vinduet, og bare
 * ruta har en side å be om. Uten `limit` er siden taket.
 */
export type CalendarItemsQuery = Omit<z.infer<typeof calendarItemsQuerySchema>, 'limit'> & {
  limit?: number | undefined;
};
export type CalendarItemsResponse = z.infer<typeof calendarItemsResponseSchema>;
export type CalendarAttendee = z.infer<typeof calendarAttendeeSchema>;
export type CalendarEventDto = z.infer<typeof calendarEventDtoSchema>;
export type CreateCalendarEventRequest = z.infer<typeof createCalendarEventRequestSchema>;
export type UpdateCalendarEventRequest = z.infer<typeof updateCalendarEventRequestSchema>;
export type RespondToCalendarEventRequest = z.infer<typeof respondToCalendarEventRequestSchema>;
export type RemoveCalendarEventQuery = z.infer<typeof removeCalendarEventQuerySchema>;
export type UpdateCalendarEventQuery = z.infer<typeof updateCalendarEventQuerySchema>;
export type CreateCalendarFeedTokenRequest = z.infer<typeof createCalendarFeedTokenRequestSchema>;
export type CalendarFeedTokenDto = z.infer<typeof calendarFeedTokenDtoSchema>;
export type CalendarFeedTokenSummary = z.infer<typeof calendarFeedTokenSummarySchema>;
export type ListCalendarFeedTokensQuery = z.infer<typeof listCalendarFeedTokensQuerySchema>;
export type CalendarFeedTokenList = z.infer<typeof calendarFeedTokenListSchema>;
export type CalendarFeedGroup = z.infer<typeof calendarFeedGroupSchema>;
export type CalendarFeedGroupKind = z.infer<typeof calendarFeedGroupKindSchema>;
